import type { JSONContent } from '@tiptap/core';
import { v4 as uuidv4 } from 'uuid';
import { normalizeImageSrcOrNull, normalizeUrlOrNull } from '../../utils/security';

export interface MarkdownToTipTapOptions {
  publicImagePathPrefix?: string;
  referenceLinks?: Map<string, { href: string; title?: string }>;
}

export class InlineParser {
  private constructor() {
    // Intentionally empty.
  }

  static parseInline(text: string, options?: MarkdownToTipTapOptions): JSONContent[] {
    return InlineParser.parseInlineWithElements(text, options, new Map());
  }

  private static parseInlineWithElements(
    text: string,
    options: MarkdownToTipTapOptions | undefined,
    elements: Map<string, { type: string; data: unknown }>,
  ): JSONContent[] {
    if (!text || !text.trim()) {
      return [];
    }

    let result = text.replace(/`([^`]+)`/g, (_match, code) => {
      const id = uuidv4();
      const placeholder = `§CODE§${id}§`;
      elements.set(placeholder, { type: 'code', data: code });
      return placeholder;
    });

    result = result.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_match, alt, src) => {
      const id = uuidv4();
      const placeholder = `§IMAGE§${id}§`;
      elements.set(placeholder, { type: 'image', data: { alt, src } });
      return placeholder;
    });

    // 1. 標準的なインラインリンク [text](url)
    result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, linkText, href) => {
      const id = uuidv4();
      const placeholder = `§LINK§${id}§`;
      elements.set(placeholder, { type: 'link', data: { text: linkText, href } });
      return placeholder;
    });

    // 2. 参照リンク (Full): [text][id]
    result = result.replace(/\[([^\]]+)\]\[([^\]]+)\]/g, (_match, linkText, refId) => {
      const def = options?.referenceLinks?.get(refId.toLowerCase());
      if (def) {
        const id = uuidv4();
        const placeholder = `§LINK§${id}§`;
        elements.set(placeholder, {
          type: 'link',
          data: {
            text: linkText,
            href: def.href,
            title: def.title,
            isReference: true,
            referenceId: refId,
          },
        });
        return placeholder;
      }
      return _match; // 定義がない場合はそのまま
    });

    // 3. 参照リンク (Collapsed or Shortcut): [text][] or [text]
    // 注意: [text] は他のブラケット記法と競合する可能性があるため、定義がある場合のみ置換
    // 後ろに ( が続かないことを明示的にチェック（順序依存を回避）
    result = result.replace(/\[([^\]]+)\](?:\[\])?(?!\()/g, (_match, linkText) => {
      const def = options?.referenceLinks?.get(linkText.toLowerCase());
      if (def) {
        const id = uuidv4();
        const placeholder = `§LINK§${id}§`;
        elements.set(placeholder, {
          type: 'link',
          data: {
            text: linkText,
            href: def.href,
            title: def.title,
            isReference: true,
            referenceId: linkText,
          },
        });
        return placeholder;
      }
      return _match;
    });

    // ReDoS対策: 最大長を制限（1000文字）
    result = result.replace(/~~([^~]{1,1000}?)~~/g, (_match, content) => {
      const id = uuidv4();
      const placeholder = `§STRIKE§${id}§`;
      elements.set(placeholder, { type: 'strike', data: content });
      return placeholder;
    });

    result = result.replace(/\*\*([^\*]{1,1000}?)\*\*/g, (_match, content) => {
      const id = uuidv4();
      const placeholder = `§BOLD§${id}§`;
      elements.set(placeholder, { type: 'bold', data: content });
      return placeholder;
    });

    result = result.replace(/\*([^*]{1,1000}?)\*/g, (_match, content) => {
      const id = uuidv4();
      const placeholder = `§ITALIC§${id}§`;
      elements.set(placeholder, { type: 'italic', data: content });
      return placeholder;
    });

    // Parse HTML img tags for rich attributes
    result = result.replace(/<img\s+([^>]+?)\/?>/gi, (_match, attributes) => {
      const id = uuidv4();
      const placeholder = `§IMAGE_HTML§${id}§`;

      // Simple attribute parser
      const getAttr = (name: string) => {
        const match = attributes.match(new RegExp(`${name}=["']([^"']*)["']`, 'i'));
        return match ? match[1] : '';
      };

      const src = getAttr('src');
      const alt = getAttr('alt');
      const style = getAttr('style');
      const widthHTML = getAttr('width');

      let align = 'center';
      let float = 'none';
      let width = widthHTML || 'auto';

      // Parse style
      if (style) {
        if (style.includes('float: left')) float = 'left';
        else if (style.includes('float: right')) float = 'right';

        if (style.includes('margin-right: auto') && !style.includes('margin-left: auto'))
          align = 'left';
        else if (style.includes('margin-left: auto') && !style.includes('margin-right: auto'))
          align = 'right';

        const widthMatch = style.match(/width:\s*([^;]+)/);
        if (widthMatch) width = widthMatch[1].trim();
      }

      elements.set(placeholder, {
        type: 'image',
        data: { alt, src, align, float, width },
      });
      return placeholder;
    });

    return InlineParser.convertToNodes(result, elements, options);
  }

  private static convertToNodes(
    text: string,
    elements: Map<string, { type: string; data: unknown }>,
    options?: MarkdownToTipTapOptions,
  ): JSONContent[] {
    const nodes: JSONContent[] = [];
    let current = '';
    let i = 0;

    while (i < text.length) {
      if (text[i] === '§') {
        if (current) {
          nodes.push({ type: 'text', text: current });
          current = '';
        }

        const endIndex = text.indexOf('§', i + 1);
        if (endIndex !== -1) {
          const secondEndIndex = text.indexOf('§', endIndex + 1);
          if (secondEndIndex !== -1) {
            const placeholder = text.substring(i, secondEndIndex + 1);
            const element = elements.get(placeholder);

            if (element) {
              switch (element.type) {
                case 'code':
                  nodes.push({
                    type: 'text',
                    marks: [{ type: 'code' }],
                    text: element.data as string,
                  });
                  break;

                case 'link': {
                  const linkData = element.data as {
                    text: string;
                    href: string;
                    title?: string;
                    isReference?: boolean;
                    referenceId?: string;
                  };
                  const safeHref = normalizeUrlOrNull(linkData.href);

                  if (!safeHref) {
                    nodes.push({
                      type: 'text',
                      text: `${linkData.text} (${linkData.href})`,
                    });
                    break;
                  }
                  nodes.push({
                    type: 'text',
                    marks: [
                      {
                        type: 'link',
                        attrs: {
                          href: safeHref,
                          title: linkData.title,
                          target: '_blank',
                          rel: 'noopener noreferrer',
                          isReference: linkData.isReference,
                          referenceId: linkData.referenceId,
                        },
                      },
                    ],
                    text: linkData.text,
                  });
                  break;
                }

                case 'image': {
                  const imageData = element.data as {
                    alt: string;
                    src: string;
                    align?: string;
                    float?: string;
                    width?: string;
                  };
                  const safeSrc = normalizeImageSrcOrNull(imageData.src, {
                    publicPathPrefix: options?.publicImagePathPrefix,
                  });

                  if (!safeSrc) {
                    nodes.push({
                      type: 'text',
                      text: `![${imageData.alt}](${imageData.src})`,
                    });
                    break;
                  }

                  nodes.push({
                    type: 'image',
                    attrs: {
                      src: safeSrc,
                      alt: imageData.alt,
                      align: imageData.align || 'center',
                      float: imageData.float || 'none',
                      width: imageData.width || 'auto',
                    },
                  });
                  break;
                }

                case 'bold':
                case 'italic':
                case 'strike': {
                  const nestedNodes = InlineParser.parseInlineWithElements(
                    element.data as string,
                    options,
                    elements,
                  );
                  for (const node of nestedNodes) {
                    if (node.type === 'text') {
                      const marks = node.marks ? [...node.marks] : [];
                      marks.unshift({ type: element.type });
                      nodes.push({ ...node, marks });
                    } else {
                      nodes.push(node);
                    }
                  }
                  break;
                }
              }
            } else {
              nodes.push({ type: 'text', text: placeholder });
            }

            i = secondEndIndex + 1;
            continue;
          }
        }
      }

      current += text[i];
      i++;
    }

    if (current) {
      nodes.push({ type: 'text', text: current });
    }

    return nodes.filter((node) => node.type !== 'text' || (node.text && node.text.length > 0));
  }
}
